import React, { useEffect, useMemo, useState } from "react";
import {
  collection,
  doc,
  getDoc,
  getDocs,
  setDoc,
  serverTimestamp,
} from "firebase/firestore";
import { db } from "../firebase";
import Sidebar from "../components/Sidebar";

export default function AdminFeaturedChoices() {
  const [vehicles, setVehicles] = useState([]);
  const [selectedIds, setSelectedIds] = useState([]);
  const [title, setTitle] = useState("Nos 3 véhicules de choix");
  const [subtitle, setSubtitle] = useState("Une sélection spéciale pour vous");
  const [isActive, setIsActive] = useState(true);
  const [search, setSearch] = useState("");
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [message, setMessage] = useState("");
  const [messageType, setMessageType] = useState("success");

  useEffect(() => {
    const fetchData = async () => {
      try {
        setLoading(true);

        const vehiclesSnap = await getDocs(collection(db, "vehicules_auto"));
        const allVehicles = vehiclesSnap.docs.map((item) => ({
          id: item.id,
          ...item.data(),
        }));
        setVehicles(allVehicles);

        const configRef = doc(db, "homepage_sections", "featured_choices");
        const configSnap = await getDoc(configRef);

        if (configSnap.exists()) {
          const data = configSnap.data();
          setSelectedIds(Array.isArray(data.vehicleIds) ? data.vehicleIds : []);
          setTitle(data.title || "Nos 3 véhicules de choix");
          setSubtitle(data.subtitle || "Une sélection spéciale pour vous");
          setIsActive(data.isActive !== false);
        }
      } catch (error) {
        console.error("Erreur de chargement :", error);
        setMessageType("danger");
        setMessage("Erreur lors du chargement des données.");
      } finally {
        setLoading(false);
      }
    };

    fetchData();
  }, []);

  const filteredVehicles = useMemo(() => {
    const term = search.trim().toLowerCase();

    return vehicles.filter((v) => {
      const text = `${v.marque || ""} ${v.modele || ""} ${v.annee || ""}`.toLowerCase();
      return text.includes(term);
    });
  }, [vehicles, search]);

  const selectedVehicles = useMemo(() => {
    return selectedIds
      .map((id) => vehicles.find((v) => v.id === id))
      .filter(Boolean);
  }, [selectedIds, vehicles]);

  const toggleVehicle = (vehicleId) => {
    setMessage("");

    if (selectedIds.includes(vehicleId)) {
      setSelectedIds((prev) => prev.filter((id) => id !== vehicleId));
      return;
    }

    if (selectedIds.length >= 3) {
      setMessageType("warning");
      setMessage("Vous ne pouvez sélectionner que 3 véhicules.");
      return;
    }

    setSelectedIds((prev) => [...prev, vehicleId]);
  };

  const moveSelected = (index, direction) => {
    const newItems = [...selectedIds];
    const targetIndex = direction === "left" ? index - 1 : index + 1;

    if (targetIndex < 0 || targetIndex >= newItems.length) return;

    [newItems[index], newItems[targetIndex]] = [newItems[targetIndex], newItems[index]];
    setSelectedIds(newItems);
  };

  const handleSave = async () => {
    setMessage("");

    if (selectedIds.length !== 3) {
      setMessageType("warning");
      setMessage("Veuillez sélectionner exactement 3 véhicules.");
      return;
    }

    try {
      setSaving(true);

      await setDoc(doc(db, "homepage_sections", "featured_choices"), {
        title,
        subtitle,
        vehicleIds: selectedIds,
        isActive,
        updatedAt: serverTimestamp(),
      });

      setMessageType("success");
      setMessage("Sélection enregistrée avec succès.");
    } catch (error) {
      console.error("Erreur enregistrement :", error);
      setMessageType("danger");
      setMessage("Erreur lors de l’enregistrement.");
    } finally {
      setSaving(false);
    }
  };

  return (
    <div className="d-flex flex-column flex-md-row">
      <Sidebar />

      <main className="flex-grow-1 p-4" style={{ marginLeft: "250px" }}>
        <div className="container-fluid">
          <div className="d-flex flex-wrap justify-content-between align-items-center gap-3 mb-4">
            <div>
              <h2 className="fw-bold mb-1">Véhicules de choix</h2>
              <p className="text-muted mb-0">
                Choisissez les 3 véhicules à mettre en avant sur le site.
              </p>
            </div>

            <button
              type="button"
              className="btn btn-success"
              onClick={handleSave}
              disabled={saving || loading}
            >
              {saving ? "Enregistrement..." : "Enregistrer la sélection"}
            </button>
          </div>

          {message && (
            <div className={`alert alert-${messageType}`} role="alert">
              {message}
            </div>
          )}

          {loading ? (
            <div className="text-center py-5">
              <div className="spinner-border text-primary" role="status">
                <span className="visually-hidden">Chargement...</span>
              </div>
            </div>
          ) : (
            <>
              <div className="row g-4 mb-4">
                <div className="col-lg-8">
                  <div className="card shadow-sm border-0">
                    <div className="card-body">
                      <h5 className="fw-bold mb-3">Configuration de la section</h5>

                      <div className="mb-3">
                        <label className="form-label">Titre</label>
                        <input
                          type="text"
                          className="form-control"
                          value={title}
                          onChange={(e) => setTitle(e.target.value)}
                          placeholder="Ex: Nos 3 véhicules de choix"
                        />
                      </div>

                      <div className="mb-3">
                        <label className="form-label">Sous-titre</label>
                        <input
                          type="text"
                          className="form-control"
                          value={subtitle}
                          onChange={(e) => setSubtitle(e.target.value)}
                          placeholder="Ex: Une sélection spéciale pour vous"
                        />
                      </div>

                      <div className="form-check form-switch">
                        <input
                          className="form-check-input"
                          type="checkbox"
                          id="featuredActiveSwitch"
                          checked={isActive}
                          onChange={(e) => setIsActive(e.target.checked)}
                        />
                        <label className="form-check-label" htmlFor="featuredActiveSwitch">
                          Activer l’affichage sur le site
                        </label>
                      </div>
                    </div>
                  </div>
                </div>

                <div className="col-lg-4">
                  <div className="card shadow-sm border-0 h-100">
                    <div className="card-body">
                      <h5 className="fw-bold mb-3">Résumé</h5>
                      <p className="mb-2">
                        <strong>Sélection :</strong> {selectedIds.length} / 3
                      </p>
                      <p className="mb-2">
                        <strong>Statut :</strong>{" "}
                        {isActive ? (
                          <span className="badge bg-success">Actif</span>
                        ) : (
                          <span className="badge bg-secondary">Inactif</span>
                        )}
                      </p>
                      <p className="text-muted small mb-0">
                        L’ordre des 3 véhicules sera respecté sur le site.
                      </p>
                    </div>
                  </div>
                </div>
              </div>

              <div className="card shadow-sm border-0 mb-4">
                <div className="card-body">
                  <h5 className="fw-bold mb-3">Véhicules sélectionnés</h5>

                  {selectedVehicles.length === 0 ? (
                    <p className="text-muted mb-0">Aucun véhicule sélectionné.</p>
                  ) : (
                    <div className="row g-3">
                      {selectedVehicles.map((v, index) => (
                        <div key={v.id} className="col-md-6 col-xl-4">
                          <div className="card border-primary h-100">
                            <img
                              src={v.imageUrls?.[0] || "/assets/default.jpg"}
                              alt={`${v.marque} ${v.modele}`}
                              className="card-img-top"
                              style={{ height: "200px", objectFit: "cover" }}
                            />

                            <div className="card-body">
                              <div className="d-flex justify-content-between align-items-start mb-2">
                                <span className="badge bg-primary">
                                  Position {index + 1}
                                </span>
                                <button
                                  type="button"
                                  className="btn btn-sm btn-outline-danger"
                                  onClick={() => toggleVehicle(v.id)}
                                >
                                  Retirer
                                </button>
                              </div>

                              <h6 className="fw-bold mb-1">
                                {v.marque} {v.modele}
                              </h6>
                              <p className="text-muted small mb-2">
                                {v.annee} • {v.ficheTechnique?.carburant || "—"} •{" "}
                                {v.ficheTechnique?.transmission || "—"}
                              </p>
                              <p className="fw-bold text-primary mb-3">
                                {parseInt(v.prix, 10) > 0
                                  ? `${parseInt(v.prix, 10).toLocaleString("fr-FR")} FCFA`
                                  : "Prix à la demande"}
                              </p>

                              <div className="d-flex gap-2">
                                <button
                                  type="button"
                                  className="btn btn-outline-secondary btn-sm w-50"
                                  onClick={() => moveSelected(index, "left")}
                                  disabled={index === 0}
                                >
                                  ← Monter
                                </button>
                                <button
                                  type="button"
                                  className="btn btn-outline-secondary btn-sm w-50"
                                  onClick={() => moveSelected(index, "right")}
                                  disabled={index === selectedVehicles.length - 1}
                                >
                                  Descendre →
                                </button>
                              </div>
                            </div>
                          </div>
                        </div>
                      ))}
                    </div>
                  )}
                </div>
              </div>

              <div className="card shadow-sm border-0">
                <div className="card-body">
                  <div className="d-flex flex-wrap justify-content-between align-items-center gap-3 mb-3">
                    <h5 className="fw-bold mb-0">Choisir les véhicules</h5>

                    <div style={{ minWidth: "280px" }}>
                      <input
                        type="text"
                        className="form-control"
                        placeholder="Rechercher une marque, un modèle, une année..."
                        value={search}
                        onChange={(e) => setSearch(e.target.value)}
                      />
                    </div>
                  </div>

                  <div className="row g-4">
                    {filteredVehicles.map((v) => {
                      const isSelected = selectedIds.includes(v.id);

                      return (
                        <div key={v.id} className="col-md-6 col-xl-4">
                          <div className={`card h-100 shadow-sm ${isSelected ? "border-primary" : ""}`}>
                            <img
                              src={v.imageUrls?.[0] || "/assets/default.jpg"}
                              alt={`${v.marque} ${v.modele}`}
                              className="card-img-top"
                              style={{ height: "220px", objectFit: "cover" }}
                            />

                            <div className="card-body d-flex flex-column">
                              <h6 className="fw-bold mb-1">
                                {v.marque} {v.modele}
                              </h6>

                              <p className="text-muted small mb-2">
                                {v.annee} • {v.ficheTechnique?.carburant || "—"} •{" "}
                                {v.ficheTechnique?.transmission || "—"}
                              </p>

                              <p className="fw-bold text-primary mb-3">
                                {parseInt(v.prix, 10) > 0
                                  ? `${parseInt(v.prix, 10).toLocaleString("fr-FR")} FCFA`
                                  : "Prix à la demande"}
                              </p>

                              <div className="mt-auto">
                                <button
                                  type="button"
                                  className={`btn w-100 ${isSelected ? "btn-primary" : "btn-outline-primary"}`}
                                  onClick={() => toggleVehicle(v.id)}
                                >
                                  {isSelected ? "Retirer de la sélection" : "Choisir ce véhicule"}
                                </button>
                              </div>
                            </div>
                          </div>
                        </div>
                      );
                    })}
                  </div>

                  {filteredVehicles.length === 0 && (
                    <div className="text-center text-muted py-4">
                      Aucun véhicule trouvé.
                    </div>
                  )}
                </div>
              </div>
            </>
          )}
        </div>
      </main>
    </div>
  );
}
